import { useCallback, useEffect, useRef, useState } from 'react';
import { isAncestorFolder } from '../../../../utils/docOrganization';
import {
  DOC_DRAG,
  FOLDER_DRAG,
  FOLDER_DRAG_IDS,
  atlasDropParentId,
  isAtlasUnfiledId,
  readDraggedFolderIds,
} from '../constants';
import {
  resolveAtlasFolderDrop,
  resolveAtlasSiblingOrder,
} from '../utils/atlasFolderDrop';

const DRAG_THRESHOLD = 8;

/**
 * Pointer-drag folders (stack under cursor) + HTML5 drops for docs/sidebar.
 */
export function useAtlasFolderDrag({
  useDemo,
  organization,
  selectedIds,
  screenToWorld,
  startDrag,
  moveDrag,
  endDrag,
  setExpandedId,
  setCtx,
  nodes = [],
}) {
  const [dropHint, setDropHint] = useState(null);
  const [draggingIds, setDraggingIds] = useState(() => new Set());
  const dragIdsRef = useRef(new Set());
  const didFolderDragRef = useRef(false);
  const folderPtrRef = useRef(null);
  const dropTargetId = dropHint?.folderId || null;

  const hitTestFolder = useCallback((clientX, clientY) => {
    const stack = document.elementsFromPoint(clientX, clientY);
    for (const el of stack) {
      const node = el.closest?.('[data-atlas-folder-id]');
      if (!node) continue;
      const id = node.getAttribute('data-atlas-folder-id');
      if (id && !dragIdsRef.current.has(id)) return id;
    }
    return null;
  }, []);

  const hintForPoint = useCallback(
    (clientX, clientY, draggedIds) => {
      const folders = organization?.folders || [];
      const targetId = hitTestFolder(clientX, clientY);
      if (targetId && !isAtlasUnfiledId(targetId)) {
        const el = document.querySelector(
          `[data-atlas-folder-id="${CSS.escape(targetId)}"]`
        );
        const rect = el?.getBoundingClientRect();
        const over = resolveAtlasFolderDrop({
          folders,
          draggedIds,
          targetId,
          clientX,
          targetRect: rect ? { left: rect.left, width: rect.width } : null,
        });
        if (over?.zone === 'middle') return over;
      }
      const world = screenToWorld(clientX, clientY);
      return resolveAtlasSiblingOrder({
        folders,
        nodes,
        draggedIds,
        dropX: world.x,
      });
    },
    [hitTestFolder, nodes, organization?.folders, screenToWorld]
  );

  const beginFolderPointer = useCallback(
    (e, folderId) => {
      if (useDemo || e.button !== 0 || e.altKey) return;
      e.stopPropagation();
      if (isAtlasUnfiledId(folderId)) return;
      didFolderDragRef.current = false;
      const ids = (
        selectedIds.has(folderId) && selectedIds.size > 1
          ? [...selectedIds]
          : [folderId]
      ).filter((id) => !isAtlasUnfiledId(id));
      if (ids.length === 0) return;
      folderPtrRef.current = {
        pointerId: e.pointerId,
        folderId,
        ids,
        startX: e.clientX,
        startY: e.clientY,
        started: false,
      };
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [useDemo, selectedIds]
  );

  const moveFolderPointer = useCallback(
    (e) => {
      const fd = folderPtrRef.current;
      if (!fd || fd.pointerId !== e.pointerId) return false;
      const dist = Math.hypot(e.clientX - fd.startX, e.clientY - fd.startY);
      if (!fd.started) {
        if (dist < DRAG_THRESHOLD) return true;
        fd.started = true;
        didFolderDragRef.current = true;
        const idSet = new Set(fd.ids);
        dragIdsRef.current = idSet;
        setDraggingIds(idSet);
        setExpandedId(null);
        setCtx(null);
        startDrag(fd.ids);
      }
      moveDrag(screenToWorld(e.clientX, e.clientY));
      setDropHint(hintForPoint(e.clientX, e.clientY, fd.ids));
      return true;
    },
    [hintForPoint, moveDrag, screenToWorld, setCtx, setExpandedId, startDrag]
  );

  const endFolderPointer = useCallback(
    (e) => {
      const fd = folderPtrRef.current;
      if (!fd || fd.pointerId !== e.pointerId) return false;
      folderPtrRef.current = null;
      try {
        e.currentTarget.releasePointerCapture?.(e.pointerId);
      } catch (_) {
        /* ignore */
      }
      if (!fd.started) return true;
      const hint = hintForPoint(e.clientX, e.clientY, fd.ids);
      dragIdsRef.current = new Set();
      setDraggingIds(new Set());
      setDropHint(null);
      if (hint && !useDemo && organization) {
        if (hint.zone === 'before' || hint.zone === 'after') {
          organization.moveFoldersNextTo?.(fd.ids, hint.folderId, hint.zone);
        } else {
          organization.moveFolders?.(fd.ids, atlasDropParentId(hint.folderId));
        }
      }
      endDrag();
      return true;
    },
    [endDrag, hintForPoint, organization, useDemo]
  );

  useEffect(() => {
    const onMove = (e) => {
      moveFolderPointer(e);
    };
    const onUp = (e) => {
      endFolderPointer(e);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [moveFolderPointer, endFolderPointer]);

  const onDragOverFolder = useCallback(
    (e, folderId) => {
      if (useDemo) return;
      const types = e.dataTransfer.types;
      const isFolderDrag =
        types.includes(FOLDER_DRAG) || types.includes(FOLDER_DRAG_IDS);
      if (!isFolderDrag && !types.includes(DOC_DRAG)) return;

      if (isFolderDrag) {
        if (isAtlasUnfiledId(folderId)) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setDropHint({ folderId, zone: 'middle' });
          return;
        }
        const moving = dragIdsRef.current;
        if (moving.size === 1 && moving.has(folderId)) return;
        const folders = organization?.folders || [];
        for (const id of moving) {
          if (id === folderId) continue;
          if (isAncestorFolder(folders, folderId, id)) return;
        }
      }

      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (isFolderDrag && dragIdsRef.current.size > 0) {
        const el = e.currentTarget;
        const rect = el?.getBoundingClientRect?.();
        setDropHint(
          resolveAtlasFolderDrop({
            folders: organization?.folders || [],
            draggedIds: [...dragIdsRef.current],
            targetId: folderId,
            clientX: e.clientX,
            targetRect: rect ? { left: rect.left, width: rect.width } : null,
          })
        );
      } else {
        setDropHint({ folderId, zone: 'middle' });
      }
    },
    [useDemo, organization?.folders]
  );

  const onDragLeaveFolder = useCallback(() => setDropHint(null), []);

  const onDropOnFolder = useCallback(
    (e, folderId) => {
      e.preventDefault();
      e.stopPropagation();
      setDropHint(null);
      if (useDemo || !organization) return;
      const draggedFolders = readDraggedFolderIds(e.dataTransfer);
      const draggedDoc = e.dataTransfer.getData(DOC_DRAG);
      if (draggedFolders.length > 0) {
        const hint = hintForPoint(e.clientX, e.clientY, draggedFolders);
        if (hint?.zone === 'before' || hint?.zone === 'after') {
          organization.moveFoldersNextTo?.(draggedFolders, hint.folderId, hint.zone);
        } else {
          organization.moveFolders?.(draggedFolders, atlasDropParentId(folderId));
        }
      } else if (draggedDoc) {
        organization.moveDocToFolder?.(draggedDoc, atlasDropParentId(folderId));
      }
    },
    [hintForPoint, useDemo, organization]
  );

  const onDropOnCanvas = useCallback((e) => {
    const types = e.dataTransfer.types;
    if (types.includes(FOLDER_DRAG) || types.includes(FOLDER_DRAG_IDS)) {
      e.preventDefault();
      setDropHint(null);
    }
  }, []);

  return {
    dropHint,
    dropTargetId,
    draggingIds,
    didFolderDragRef,
    folderPtrRef,
    beginFolderPointer,
    moveFolderPointer,
    endFolderPointer,
    onDragOverFolder,
    onDragLeaveFolder,
    onDropOnFolder,
    onDropOnCanvas,
  };
}
